import { createSelector, createSlice, nanoid, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "setup";
import {
  ChatDocumentModel,
  ChatMessageModel,
  IMessageFeedbackModel,
  QueryResultModel,
} from "services/api/cplapi/CPLAPI";
import { toISOStringIfValid } from "global/dayjs";
import {
  deleteChatDocument,
  deleteChatSession,
  getChatSessions,
  loadChatSession,
  removeMessageFeedback,
  renameChatSession,
  sendChatMessage,
  setMessageFeedback,
  uploadFile,
} from "app/modules/aiAssistant/AIAssistantThunks";

export const NEW_CHAT_NAME = "New Chat";

// Note: one document per chat until the model can read more than one
const MAXIMUM_DOCUMENTS_PER_CHAT = 1;

// Matches the API's AIChatRole
const USER_ROLE = 0;

export type ChatDocument = {
  id: string;
  name: string;
};

export type ChatMessage = {
  content: string;
  isUserAuthor: boolean;
  timestamp?: string;
  queryResults?: { [key: string]: QueryResultModel } | undefined;
  messageID?: number;
  feedback?: IMessageFeedbackModel;
  documents?: ChatDocument[];
};

const toChatDocument = (document: ChatDocumentModel): ChatDocument => ({
  id: document.documentID!,
  name: document.name ?? "",
});

const toChatMessage = (message: ChatMessageModel): ChatMessage => ({
  content: message.message ?? "",
  isUserAuthor: message.role === USER_ROLE,
  timestamp: toISOStringIfValid(message.timestamp),
  queryResults: message.queryResults,
  messageID: message.messageID,
  feedback: message.feedback ? { ...message.feedback } : undefined,
  documents: message.documents?.length ? message.documents.map(toChatDocument) : undefined,
});

// A chat in the chat list
export type ChatSummary = {
  chatSessionID: string;
  chatName: string;
  startedAt?: string;
};

// The chat on screen. The key changes whenever another chat is opened or a new one started,
// so replies and uploads that finish after the user has moved on are ignored
export type Conversation = {
  key: string;
  chatSessionID: string | null;
};

type AIAssistantState = {
  Chats: ChatSummary[];
  LoadingChats: boolean;
  // The chat list can be fetched again before the last fetch answers, and only the newest counts
  LatestGetChatSessionsRequestID: string | null;
  AssistantOpen: boolean;
  Conversation: Conversation;
  Messages: ChatMessage[];
  AwaitingReply: boolean;
  LoadingChat: boolean;
  UploadingDocumentIDs: string[];
  // Every document in the chat, including ones already sent
  ChatDocuments: ChatDocument[];
  // Documents waiting in the input to go with the next message
  UnsentDocumentIDs: string[];
  SessionStartedAt: string | undefined;
  CurrentRunID: string | null;
  RunSteps: string[];
};

const emptyConversation = {
  Messages: [],
  AwaitingReply: false,
  LoadingChat: false,
  UploadingDocumentIDs: [],
  ChatDocuments: [],
  UnsentDocumentIDs: [],
  SessionStartedAt: undefined,
  CurrentRunID: null,
  RunSteps: [],
};

const initialState: AIAssistantState = {
  Chats: [],
  LoadingChats: false,
  LatestGetChatSessionsRequestID: null,
  AssistantOpen: false,
  Conversation: { key: nanoid(), chatSessionID: null },
  ...emptyConversation,
};

const isCurrent = (state: AIAssistantState, conversation: Conversation) => state.Conversation.key === conversation.key;

const AIAssistantSlice = createSlice({
  name: "AIAssistant",
  initialState,
  reducers: {
    startNewChat: {
      reducer: (state, { payload }: PayloadAction<string>) => ({
        ...state,
        ...emptyConversation,
        Conversation: { key: payload, chatSessionID: null },
      }),
      prepare: () => ({ payload: nanoid() }),
    },
    setAssistantOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.AssistantOpen = payload;
    },
    // Dispatched by the AssistimoRunStep SignalR handler in SignalRNotificationHelpers.ts
    addRunStep: (state, { payload }: PayloadAction<{ runID: string; step: string }>) => {
      // Ignore steps from other tabs' runs, ones that arrive after the reply, and repeats
      if (payload.runID !== state.CurrentRunID || state.RunSteps.at(-1) === payload.step) return;
      state.RunSteps.push(payload.step);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatSessions.pending, (state, action) => {
        state.LoadingChats = true;
        state.LatestGetChatSessionsRequestID = action.meta.requestId;
      })
      .addCase(getChatSessions.rejected, (state, action) => {
        if (state.LatestGetChatSessionsRequestID !== action.meta.requestId) return;
        state.LoadingChats = false;
      })
      .addCase(getChatSessions.fulfilled, (state, action) => {
        if (state.LatestGetChatSessionsRequestID !== action.meta.requestId) return;
        state.LoadingChats = false;
        state.Chats = action.payload.map((c) => ({
          chatSessionID: c.chatSessionID!,
          chatName: c.chatName ?? NEW_CHAT_NAME,
          startedAt: toISOStringIfValid(c.startedAt),
        }));
      })
      .addCase(deleteChatSession.fulfilled, (state, action) => {
        state.Chats = state.Chats.filter((c) => c.chatSessionID !== action.meta.arg);
      })
      .addCase(renameChatSession.fulfilled, (state, action) => {
        const { chatSessionID, newChatName } = action.meta.arg;
        const chat = state.Chats.find((c) => c.chatSessionID === chatSessionID);
        if (chat) chat.chatName = newChatName;
      })
      .addCase(loadChatSession.pending, (state, action) => ({
        ...state,
        ...emptyConversation,
        LoadingChat: true,
        Conversation: { key: action.meta.requestId, chatSessionID: action.meta.arg },
      }))
      .addCase(loadChatSession.rejected, (state, action) => {
        if (state.Conversation.key !== action.meta.requestId) return;
        state.LoadingChat = false;
      })
      .addCase(loadChatSession.fulfilled, (state, action) => {
        if (state.Conversation.key !== action.meta.requestId) return;
        state.LoadingChat = false;
        state.SessionStartedAt = toISOStringIfValid(action.payload.startedAt);
        state.Messages = (action.payload.messages ?? []).map(toChatMessage);
        state.ChatDocuments = (action.payload.documents ?? []).map(toChatDocument);
        const sentDocumentIDs = state.Messages.flatMap((m) => m.documents ?? []).map((d) => d.id);
        state.UnsentDocumentIDs = state.ChatDocuments.map((d) => d.id).filter((id) => !sentDocumentIDs.includes(id));
      })
      .addCase(sendChatMessage.pending, (state, action) => {
        if (!isCurrent(state, action.meta.arg.conversation)) return;
        state.AwaitingReply = true;
        state.CurrentRunID = action.meta.requestId;
        state.RunSteps = [];
        const documents = state.ChatDocuments.filter((d) => state.UnsentDocumentIDs.includes(d.id));
        state.Messages.push({
          content: action.meta.arg.message,
          timestamp: new Date().toISOString(),
          isUserAuthor: true,
          documents: documents.length ? documents : undefined,
        });
        state.UnsentDocumentIDs = [];
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        if (!isCurrent(state, action.meta.arg.conversation)) return;
        state.AwaitingReply = false;
        state.CurrentRunID = null;
        state.RunSteps = [];

        // The API saves nothing for a stopped or failed question, so it leaves the chat
        // and its documents go back in the input
        if (state.Messages[state.Messages.length - 1]?.isUserAuthor) {
          const stopped = state.Messages.pop();
          state.UnsentDocumentIDs.push(...(stopped?.documents ?? []).map((d) => d.id));
        }
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        if (!isCurrent(state, action.meta.arg.conversation)) return;
        state.AwaitingReply = false;
        state.CurrentRunID = null;
        state.RunSteps = [];
        state.Conversation.chatSessionID = action.payload.chatSessionID!;
        state.Messages.push(toChatMessage(action.payload.reply!));
      })
      .addCase(uploadFile.pending, (state, action) => {
        if (!isCurrent(state, action.meta.arg.conversation)) return;
        const { file } = action.meta.arg;
        state.ChatDocuments.push({ id: action.meta.requestId, name: file.name });
        state.UploadingDocumentIDs.push(action.meta.requestId);
        state.UnsentDocumentIDs.push(action.meta.requestId);
      })
      .addCase(uploadFile.rejected, (state, action) => {
        if (!isCurrent(state, action.meta.arg.conversation)) return;
        state.UploadingDocumentIDs = state.UploadingDocumentIDs.filter((id) => id !== action.meta.requestId);
        state.ChatDocuments = state.ChatDocuments.filter((d) => d.id !== action.meta.requestId);
        state.UnsentDocumentIDs = state.UnsentDocumentIDs.filter((id) => id !== action.meta.requestId);
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        if (!isCurrent(state, action.meta.arg.conversation)) return;
        state.UploadingDocumentIDs = state.UploadingDocumentIDs.filter((id) => id !== action.meta.requestId);
        state.Conversation.chatSessionID = action.payload.chatSessionID!;
        const documentID = action.payload.documentID!;
        state.UnsentDocumentIDs = state.UnsentDocumentIDs.map((id) => (id === action.meta.requestId ? documentID : id));

        // Uploading a file the chat already has returns the existing document
        if (state.ChatDocuments.some((d) => d.id === documentID)) {
          state.ChatDocuments = state.ChatDocuments.filter((d) => d.id !== action.meta.requestId);
        } else {
          const document = state.ChatDocuments.find((d) => d.id === action.meta.requestId);
          if (document) document.id = documentID;
        }
      })
      .addCase(deleteChatDocument.fulfilled, (state, action) => {
        const documentID = action.meta.arg;
        state.ChatDocuments = state.ChatDocuments.filter((d) => d.id !== documentID);
        state.UnsentDocumentIDs = state.UnsentDocumentIDs.filter((id) => id !== documentID);
        state.Messages.forEach((m) => {
          const documents = m.documents?.filter((d) => d.id !== documentID);
          m.documents = documents?.length ? documents : undefined;
        });
      })
      .addCase(setMessageFeedback.fulfilled, (state, action) => {
        const message = state.Messages.find((m) => m.messageID === action.meta.arg.messageID);
        if (message) message.feedback = action.meta.arg.feedback;
      })
      .addCase(removeMessageFeedback.fulfilled, (state, action) => {
        const message = state.Messages.find((m) => m.messageID === action.meta.arg);
        if (message) message.feedback = undefined;
      });
  },
});

export const { startNewChat, setAssistantOpen, addRunStep } = AIAssistantSlice.actions;

export const selectChats = (state: RootState) => state.AIAssistant.Chats;
export const selectLoadingChats = (state: RootState) => state.AIAssistant.LoadingChats;
export const selectAssistantOpen = (state: RootState) => state.AIAssistant.AssistantOpen;
export const selectConversation = (state: RootState) => state.AIAssistant.Conversation;
export const selectCurrentChatSessionID = (state: RootState) => state.AIAssistant.Conversation.chatSessionID;
const selectCurrentChat = (state: RootState) =>
  state.AIAssistant.Chats.find((c) => c.chatSessionID === state.AIAssistant.Conversation.chatSessionID);
export const selectCurrentChatName = (state: RootState) => selectCurrentChat(state)?.chatName;
export const selectMessages = (state: RootState) => state.AIAssistant.Messages;
export const selectAwaitingReply = (state: RootState) => state.AIAssistant.AwaitingReply;
export const selectRunSteps = (state: RootState) => state.AIAssistant.RunSteps;
export const selectCurrentRunID = (state: RootState) => state.AIAssistant.CurrentRunID;
export const selectLoadingChat = (state: RootState) => state.AIAssistant.LoadingChat;
export const selectUploadingDocumentIDs = (state: RootState) => state.AIAssistant.UploadingDocumentIDs;
export const selectChatDocuments = (state: RootState) => state.AIAssistant.ChatDocuments;
const selectUnsentDocumentIDs = (state: RootState) => state.AIAssistant.UnsentDocumentIDs;
export const selectUnsentDocuments = createSelector(
  [selectChatDocuments, selectUnsentDocumentIDs],
  (documents, unsentIDs) => documents.filter((d) => unsentIDs.includes(d.id))
);
export const selectDocumentsLeftToAttach = (state: RootState) =>
  Math.max(0, MAXIMUM_DOCUMENTS_PER_CHAT - state.AIAssistant.ChatDocuments.length);
// A chat saved during this visit hasn't been loaded, so its start comes from the chat list
export const selectSessionStartedAt = (state: RootState) =>
  state.AIAssistant.SessionStartedAt ?? selectCurrentChat(state)?.startedAt;

export default AIAssistantSlice.reducer;
